import {
  Button,
  theme as defaultTheme,
  Icon,
  useSnackbar,
} from "@mindlercare/ui-web";
import { MessageTable } from "components/messages/messagesTable";
import { useImplementedFeature } from "core/selectors/app";
import { useUserHasRole } from "hooks/userHooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "styled-components";

import { LanguageCodeISO2 } from "@mindlercare/schema";
import BackButton from "components/lib/backButton";
import { Message } from "components/messages/message.types";
import { SendMessageModal } from "components/messages/MessageModal/SendMessageModal";
import { Typography } from "components/typography";

import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
import { useMindlerApi } from "api/apiv2";
import { DeleteMessageModal } from "components/messages/MessageModal/DeleteMessageModal";
import { ViewMessageModal } from "components/messages/MessageModal/ViewMessageModal";
import {
  useGetPatientMessages,
  useGetPatientMessageTemplates,
} from "hooks/messages";
import { DEFAULT_LANG as APP_LANGUAGE } from "../helpers/language";

const { useMutation, useQuery } = ReactQueryV5;

const Container = styled.div`
  padding: 32px;
  min-height: 100px;
  max-width: 1500px;

  td {
    white-space: nowrap;
  }
`;

const TopBarContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Paragraph = styled(Typography)`
  color: ${(p) => defaultTheme.COLORS.GRAY_DARK};
  margin-top: 8px;
`;

const ButtonWrapper = styled.div`
  padding: 0 20px;

  .iconButton {
    padding: 0 20px;
  }
`;

const StyledButtonText = styled(Typography)`
  margin-left: 4px;
`;

type MessageType = "new";

export const Messages = () => {
  const navigate = useNavigate();
  const { userId, type } = useParams<{ userId: string; type?: MessageType }>();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [messageSelectedId, setMessageSelectedId] = useState<string | null>(
    null,
  );

  const [selectedTemplateType, setSelectedTemplateType] =
    useState<Message | null>(null);

  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);

  const isAdminOrPsychologist = useUserHasRole(["Admin", "Psychologist"]);
  const [isMessagingEnabled] = useImplementedFeature("MESSAGING_TO_PATIENT");

  const apiv2 = useMindlerApi();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(userId),
      },
      {
        enabled: !!userId,
      },
    ),
  );

  const { data: messageTemplates = [] } = useGetPatientMessageTemplates(
    patient?.userId,
  );

  const { data: patientMessages = [], refetch: refetchPatientMessages } =
    useGetPatientMessages(patient?.userId);

  const { mutateAsync: sendPsychologistMessage, isPending: isMutating } =
    useMutation(apiv2.messages.sendPsychologistMessage.mutationOptions());

  const setMessageDeliveryError = () => {
    navigate(`/messages/${userId}`, { replace: true });
    enqueueSnackbar({
      text: t("MESSAGE_FAILURE_NOTIFICATION_TEXT"),
      isError: true,
    });
  };

  const postMessageToPatient = async ({
    overrideMessageBody,
    attachmentKeys,
  }: {
    overrideMessageBody?: string;
    attachmentKeys?: string[];
  }) => {
    const receiverId = patient?.userId;
    const templateName = selectedTemplateType?.templateType;
    try {
      if (!receiverId) throw new Error("No receiver userId");
      if (!templateName) throw new Error("No template name");
      await sendPsychologistMessage({
        patientUserId: Number(receiverId),
        templateName,
        senderLanguageCodeISO2: APP_LANGUAGE as LanguageCodeISO2,
        freeText: overrideMessageBody,
        ...(attachmentKeys ? { attachmentKeys } : {}),
        ...(attachmentKeys ? { attachmentKey: attachmentKeys[0] } : {}),
      });
      navigate(`/messages/${userId}`, { replace: true });
      await refetchPatientMessages();
      enqueueSnackbar({
        text: t("MESSAGE_SUCCESS_NOTIFICATION_TEXT", {
          patient: `${patient?.firstName} ${patient?.lastName}`,
        }),
      });
      setSelectedTemplateType(null);
    } catch {
      setMessageDeliveryError();
    }
  };

  if (!isMessagingEnabled || !isAdminOrPsychologist || !patient) return null;

  return (
    <Container>
      <TopBarContainer>
        <BackButton />
        <ButtonWrapper>
          <Button
            disabled={false}
            onClick={() =>
              navigate(`/messages/${userId}/new`, { replace: true })
            }
            id="newMessageButton"
            variant="primary"
            testId="newMessageButton"
          >
            <Icon.Add />
            <StyledButtonText insensitive variant="body">
              {t("BUTTON_NEW_MESSAGE")}
            </StyledButtonText>
          </Button>
        </ButtonWrapper>
      </TopBarContainer>
      <Typography variant="h1" data-testid="cy-page-header">
        {t("PAGE_HEADER_MESSAGES", {
          patient: `${patient.firstName} ${patient.lastName}`,
        })}
      </Typography>
      <Paragraph variant="body">{t("PAGE_SUB_PARAGRAPH_MESSAGES")}</Paragraph>
      <MessageTable
        data={patientMessages}
        onClick={(m) => setMessageSelectedId(m)}
        onDelete={(messageId) => setMessageToDelete(messageId)}
      />
      <DeleteMessageModal
        open={!!messageToDelete}
        onClose={() => setMessageToDelete(null)}
        messageId={messageToDelete}
        patientUserId={patient.userId}
      />
      <ViewMessageModal
        message={
          patientMessages.find((m) => m.id === messageSelectedId) ?? null
        }
        onClose={() => setMessageSelectedId(null)}
        patientName={`${patient.firstName} ${patient.lastName}`}
      />
      <SendMessageModal
        setModalIsOpen={(isOpen) =>
          isOpen
            ? navigate(`/messages/${userId}/new`, { replace: true })
            : navigate(`/messages/${userId}`, { replace: true })
        }
        patientName={`${patient.firstName} ${patient.lastName}`}
        patientUserId={patient.userId}
        open={type === "new"}
        postMessageToPatient={postMessageToPatient}
        setSelectedTemplateType={setSelectedTemplateType}
        messageTemplates={messageTemplates}
        selectedTemplateType={selectedTemplateType}
        isMutating={isMutating}
      />
    </Container>
  );
};
